import { Button, Stack, Typography } from '@mui/material'
import { DateTimePickerField, TextInputField } from 'utoniq-ui'

import { ArtistCreateDmRoomInput } from '@/clients/utoniq-core/schema'
import {
  handleError,
  tLabel,
  tMessage,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { convertDatePickerValueToDate, dayjs } from '@/lib/dayjs'

type DmRoomCreateValues = {
  title: string
  startedAt: string
  expiredAt: string
  accountIds: string[]
}

const schema: UseFormProps['schema'] = o => ({
  title: o.string().min(1).max(30).label(tLabel('consoleDmRoomCreate.title')),
  startedAt: o
    .date()
    .custom((value: string, helpers) => {
      if (!value) return value
      const startDate = new Date(value)
      const now = new Date()

      // 未来の日付かチェック
      if (startDate <= now) {
        return helpers.message({
          custom: tMessage('consoleDmRoomCreate.startedAt.custom.future'),
        })
      }

      return value
    })
    .label(tLabel('consoleDmRoomCreate.startedAt')),
  expiredAt: o
    .date()
    .custom((value: string, helpers) => {
      if (!value) return value
      const expiredDate = new Date(value)
      const now = new Date()

      // 未来の日付かチェック
      if (expiredDate <= now) {
        return helpers.message({
          custom: tMessage('consoleDmRoomCreate.expiredAt.custom.future'),
        })
      }

      // startedAtより後の日付かチェック
      const [data] = helpers?.state?.ancestors || []
      const startedAt = data?.startedAt
      if (startedAt) {
        const startDate = new Date(startedAt)
        if (expiredDate <= startDate) {
          return helpers.message({
            custom: tMessage('consoleDmRoomCreate.expiredAt.custom.over'),
          })
        }
      }

      return value
    })
    .label(tLabel('consoleDmRoomCreate.expiredAt')),
  accountIds: o.array().items(
    o
      .string()
      .min(1)
      .pattern(/^[a-zA-Z0-9_]*$/)
      .label(tLabel('consoleDmRoomCreate.accountIds'))
  ),
})

const defaultValues: DmRoomCreateValues = {
  title: '',
  startedAt: '',
  expiredAt: '',
  accountIds: [],
}

type DmRoomCreateFormProps = {
  onSubmit: (values: ArtistCreateDmRoomInput) => Promise<void>
}

export const DmRoomCreateForm: Organism<DmRoomCreateFormProps> = ({
  onSubmit,
}) => {
  const { t } = useTranslation('consoleDmRoomCreate')
  const { showError } = useMessage()
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    setError,
  } = useForm<DmRoomCreateValues>({
    schema,
    defaultValues,
  })

  const handleOnSubmit = handleSubmit(async values => {
    await onSubmit(values).catch(error => {
      handleError<DmRoomCreateValues>(error, setError, showError)
    })
  })

  const startedAt = watch('startedAt')
  const expiredAt = watch('expiredAt')

  return (
    <OrganismFrame sx={{ pt: 5 }}>
      <CellFrame>
        <TextInputField
          type="text"
          {...register('title')}
          titleProps={{
            display: 'flex',
            alignItems: 'center',
            variant: 'h5',
            color: 'text.primary',
          }}
          error={errors?.title}
          t={t}
        />
      </CellFrame>
      <CellFrame sx={{ pb: 5 }}>
        <Typography variant="h5" pb={2}>
          {t('usagePeriod')}
        </Typography>
        <DateTimePickerField
          onChange={date => {
            setValue(
              'startedAt',
              (convertDatePickerValueToDate(date) || '') as never,
              { shouldValidate: true, shouldDirty: true }
            )
          }}
          value={startedAt ? dayjs(startedAt) : null}
          title={t('startedAt')}
          placeholder={t('startedAtPlaceholder')}
          error={errors?.startedAt}
          titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
        />
        <DateTimePickerField
          onChange={date => {
            setValue(
              'expiredAt',
              (convertDatePickerValueToDate(date) || '') as never,
              { shouldValidate: true, shouldDirty: true }
            )
          }}
          value={expiredAt ? dayjs(expiredAt) : null}
          title={t('expiredAt')}
          placeholder={t('expiredAtPlaceholder')}
          error={errors?.expiredAt}
          titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
        />
      </CellFrame>
      <CellFrame>
        <TextInputField
          type="text"
          {...register('accountIds.0')}
          titleProps={{
            children: t('accountIdsTitle'),
            display: 'flex',
            alignItems: 'center',
            variant: 'h5',
            color: 'text.primary',
          }}
          textFieldProps={{
            placeholder: t('accountIdsPlaceholder'),
          }}
          error={errors?.accountIds?.[0]}
          t={t}
        />
      </CellFrame>
      <Stack alignItems="center" pb={6}>
        <Button
          size="large"
          sx={{ width: 212 }}
          loading={isSubmitting}
          disabled={!isValid}
          onClick={handleOnSubmit}
        >
          {t('create')}
        </Button>
      </Stack>
    </OrganismFrame>
  )
}
